import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import PageApp from 'pageApp';
import TagManagerUtil from '@component/common/src/util/TagManagerUtil';
import LOGGER from '@component/common/src/util/Logger';
import Loader from '@component/common/src/components/Loader';
import ProsEventHandler from '../events/ProsEventHandler';
import AddToListHandler from '../events/AddToListEventHandler';
import ProsTrackingHelper from '../util/ProsTrackingHelper';
import ProductThumbnailFactory from '../factory/ProductThumbnailFactory';

const PageAppChannel = PageApp.channel;

/**
 * This class responsible to create a pros panle on the page.
 * Dependency Tree
 *  CarouselCreator
 *  |
 *  |
 *  @component/product-pool-media
 *  |          |
 *  |          @component/carousel
 *  |
 *  @component/product-thumbnail
 *
 *
 *  Input it needs to create carousel:
 *  config:{
 *      element: 'jquery-selector where carousel will be created',
 *      orientation: 'vertical'/'horizontal',
 *      autoScroll:true/false,
 *      pageSize: no of thumbs to show in a carousel,
 *      pixelPresentedEnabled:true/false,
 *  },
 *  properties:{},
 *
 *  json:{
 *      recommendedIdsList:[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15],//(Array of product-ids)
 *      headerText:'you may like this',//(text to show in carousel header)
 *      encodedChoiceId:'choiceId string returned by pros rto service for analytics purpose'
 *  },
 *  thumbnailCollection,
 *  thumbnailModel
 */
export default class Panel {
  constructor({
    config,
    properties,
    json,
    thumbnailCollection,
    thumbnailModel,
  }) {
    if (!config || !json || !config.element) {
      return;
    }
    this.json = json;
    this.minimumProductsToDisplay = config.minimumProductsToDisplay || 1;
    this.thumbnailCollectionClass = thumbnailCollection;
    this.thumbnailModel = thumbnailModel;
    this.thumbnailView = ProductThumbnailFactory.getView(properties, config);
    this.id = config.uniqueId;
    this.config = config;
    this.properties = properties;
    this.$el = $(config.element);
    this.pagination = { pageSize: config.pageSize || 6, currentPage: 1 };
    this.config.headerText = json.headerText;
    this.config.encodedChoiceId = json.encodedChoiceId;
    this.collectionBuffer = new Backbone.Collection();
    this.prosEventHandler = new ProsEventHandler();
    this.meta = json.meta || '';
    this.loadCollection(json);
  }

  loadCollection(json) {
    this.thumbnailCollection = this.thumbnailCollectionClass.fromArray(
      json.recommendedIdsList,
      Object.assign({
        model: this.thumbnailModel,
      }, this.config),
      this.properties,
    );
  }

  getCollectionSize() {
    return this.thumbnailCollection.size();
  }

  registerEvents() {
    const { $el, thumbnailCollection: collection } = this;
    const { zone, headerText, encodedChoiceId: choiceId } = this.config;

    ProsEventHandler.registerAnalytics({
      $el,
      choiceId,
      collection,
      headerText,
      zone,
    });

    ProsEventHandler.registerHeartingAdobeAnalytics({
      $el,
      choiceId,
      collection,
      headerText,
      zone,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  registerEventsOnRender() {

  }

  getPanelData(items) {
    this.collectionBuffer.reset(items, { silent: true });

    // call @product-pool-media to render component
    const productPoolCollection = new Backbone.Model({
      productPoolMedia: {
        brand: PageApp.brand,
        poolLink: {
          text: this.config.headerText,
        },
      },
    });

    productPoolCollection.get('productPoolMedia').collection = this.collectionBuffer;
    if (this.config.orientation === 'horizontal' && this.meta) {
      productPoolCollection.get('productPoolMedia').stitches = {
        showStitches: this.properties.mltStitches,
        meta: this.meta,
      };
    }

    if (this.properties.mltBagUnavailable) {
      productPoolCollection.get('productPoolMedia').mltBagUnavailable = true;
    }
    return productPoolCollection;
  }

  preprocessThumbs(thumbs) {
    return thumbs;
  }

  render() {
    const { currentPage, pageSize } = this.pagination;
    const isRecByProductTypeEnabled = this.properties.prosCollectionDistProductTypeEnabled || false;
    const prosCollectionEnabledForZone = (this.properties.prosCollectionsEnabled && this.json.content && this.json.content.isCollection) || false;
    if (isRecByProductTypeEnabled && prosCollectionEnabledForZone) {
      const splitRecommendations = [].concat(this.json.recommendedIdsList);
      this.thumbnailCollection.loadPage(currentPage, this.json.recommendedIdsList.length)
        .then((thumbs) => {
          const baseList = thumbs.map((model) => {
            const item = splitRecommendations.find(x => x.id === model.get('id'));
            model.set('productType', item.productType);
            return model;
          });
          const groups = _.uniq(baseList.map(item => item.get('productType')));
          const result = thumbs.reduce((acc) => {
            function getRandomItem(ele) {
              const groupName = groups[ele.groupIdx];
              const randomItem = baseList.find(item => item.get('productType') === groupName);
              const itemIdx = baseList.findIndex(item => item.get('productType') === groupName);
              if (!randomItem) {
                ele.groupIdx++;
                if (ele.groupIdx >= groups.length) {
                  ele.groupIdx = 0;
                }
                return getRandomItem(ele);
              }
              return { randomItem, itemIdx };
            }

            const { randomItem, itemIdx } = getRandomItem(acc);
            acc.set.push(randomItem);
            baseList.splice(itemIdx, 1);
            acc.groupIdx++;

            if (acc.groupIdx >= groups.length) {
              acc.groupIdx = 0;
            }
            return acc;
          }, { groupIdx: 0, set: [] });

          thumbs = result.set.slice(0, 6); // eslint-disable-line no-param-reassign

          if (!thumbs || thumbs.length < this.minimumProductsToDisplay) {
            PageAppChannel.trigger('pros-carousel-render-failed', this.config);
            this.$el.hide();
            return;
          }
          const { requester, zone } = this.config;
          thumbs = this.preprocessThumbs(thumbs); // eslint-disable-line no-param-reassign

          const updatedThumbs = ProsTrackingHelper.addAnalyticsInfo({
            requester, zone, pageSize, currentPage, thumbs,
          });
          this.createPanel({ items: updatedThumbs });
          // LAST_PAGE_EVENT should be registered before this.carouselView.render()
          this.registerEvents();
          this.panel.render();
          this.onRender(updatedThumbs);
        }).catch((error) => {
          PageAppChannel.trigger('pros-carousel-render-failed', this.config);
          this.$el.hide();
          LOGGER.error('Pros-factory Panel.js render() error in creating panel is:', error);
        });
    } else {
      this.thumbnailCollection.loadPage(currentPage, pageSize)
        .then((thumbs) => {
          if (!thumbs || thumbs.length < this.minimumProductsToDisplay) {
            PageAppChannel.trigger('pros-carousel-render-failed', this.config);
            this.$el.hide();
            return;
          }
          const { requester, zone } = this.config;
          thumbs = this.preprocessThumbs(thumbs); // eslint-disable-line no-param-reassign
          const updatedThumbs = ProsTrackingHelper.addAnalyticsInfo({
            requester, zone, pageSize, currentPage, thumbs,
          });
          this.createPanel({ items: updatedThumbs });
          // LAST_PAGE_EVENT should be registered before this.carouselView.render()
          this.registerEvents();
          this.panel.render();
          this.onRender(updatedThumbs);
        }).catch((error) => {
          PageAppChannel.trigger('pros-carousel-render-failed', this.config);
          this.$el.hide();
          LOGGER.error('Pros-factory Panel.js render() error in creating panel is:', error);
        });
    }
  }

  onRender(thumbs) {
    this.$el.find('.adText').attr('class', 'poolHeaderText');
    this.triggerEvent('pros-carousel-rendered', { thumbs });
    ProsEventHandler.bindThumbnailOverHandler(
      this.$el,
      AddToListHandler.canShowALTMsg(this.properties.prosZonesAddToListEnabled),
      thumbs,
    );
    if (AddToListHandler.canShowALTMsg(this.properties.prosZonesAddToListEnabled)) {
      AddToListHandler.bindAddToListListener(this.$el);
    }
    this.registerEventsOnRender();
    this.$el.find('.poolHeader').attr('aria-live', 'polite');
    if (this.config.orientation === 'horizontal' && this.properties.mltStitches && this.meta) {
      this.mltStitchesWeights(this.$el);
      if (!this.properties.weightStatus) {
        this.fireStitchesLinkTagging();
      }
    }

    if (this.properties.mltBagUnavailable) {
      const prodCount = this.json.recommendedIdsList.length;
      const prosContainer = $(`#itemPros-${this.config.productId}`);
      let modelName = '';
      modelName = this.json.choiceId ? this.json.choiceId.split('-')[0].slice(-3) : '';
      if ($(`#itemPros-${this.config.productId} .product-pool-media-container .carousel-content`)) {
        prosContainer.attr({ 'data-count': prodCount, 'data-modelname': modelName }).removeClass('hideProsAccordion');
      }
    }
  }

  mltStitchesWeights(ppmContainer) {
    const chipEle = $('.chips');
    $('.page-navigation.footer').hide();
    chipEle.off('click').on('click', (e) => {
      Loader.show(ppmContainer);
      $(e.currentTarget).attr(
        `data-${e.currentTarget.id}`,
        $(e.currentTarget).attr(`data-${e.currentTarget.id}`) === 'false' ? 'true' : 'false',
      ).toggleClass('stitches-inactive');
      const prosConfig = {};
      const weightValues = {};
      const weightChip = $('#mlt-weights');
      const selectedChip = $(e.currentTarget);
      const chipState = selectedChip.attr(`data-${e.currentTarget.id}`) === 'false' ? 'off' : 'on';
      const slotId = `R00S${selectedChip.attr('data-slot')}`;
      const attr = `chip state:${chipState}`;
      const weightName = selectedChip.find('.weightName').html();

      prosConfig.properties = {
        prosFacetFilterPhase2Enabled: this.properties.prosFacetFilterPhase2Enabled,
        mltStitches: true,
        weightStatus: chipState,
      };
      prosConfig.panelConfig = {
        element: $('.mltStitches').parent().parent(),
      };
      prosConfig.recommendationConfig = {
        productId: weightChip.data('prodid'),
        categoryId: weightChip.data('catid'),
        imageId: weightChip.data('imgid'),
      };
      prosConfig.requester = this.config.requester;
      prosConfig.zone = 'PDP_ZONE_D';

      chipEle.each((index, ele) => {
        weightValues[ele.id] = $(ele).attr(`data-${ele.id}`);
      });
      if (!_.isEmpty(weightValues)) {
        prosConfig.recommendationConfig.weights = JSON.stringify(weightValues);
      }


      TagManagerUtil.fireTag('link', {
        event_name: 'content link click',
        impression_data: [{
          method_type: 'G',
          link_element: weightName,
          content_position: slotId,
          zone_type: 'pros',
          zone_title: 'visually similar',
          media_type: 'chips',
          attribute1: attr,
        }],
      });
      PageApp.channel.trigger('pros-factory:render:zone', prosConfig);
    });
  }

  fireStitchesLinkTagging() {
    const expData = [];
    if ($('.chips').length > 0) {
      $('.chips').each((key, element) => {
        const chipState = $(element).attr(`data-${element.id}`) === 'false' ? 'off' : 'on';
        const weightName = $(element).find('.weightName').html();
        const slodId = `R00S${$(element).data('slot')}`;
        const impressionData = {
          method_type: 'G',
          link_element: weightName,
          content_position: slodId,
          zone_type: 'pros',
          zone_title: 'visually similar',
          media_type: 'chips',
          attribute1: `chip state:${chipState}`,
        };
        expData.push(impressionData);
      });

      const uTagData = {
        event_name: 'content view',
        impression_data: expData,
      };

      TagManagerUtil.fireTag('link', uTagData);
    }
  }

  onScroll(thumbs) {
    this.triggerEvent('pros-carousel-scrolled');
    ProsEventHandler.bindThumbnailOverHandler(
      this.$el, AddToListHandler.canShowALTMsg(this.properties.prosZonesAddToListEnabled),
      thumbs,
    );
  }

  triggerEvent(eventName, merge) {
    const { requester, zone } = this.config;
    let evtParams = {
      requester,
      zone,
      uniqueId: this.id,
    };
    // in case of further customization needed
    if (typeof merge === 'object') {
      Object.assign(evtParams, merge);
    } else if (typeof merge === 'function') {
      evtParams = merge(evtParams);
    }
    PageAppChannel.trigger(eventName, evtParams);
    PageAppChannel.trigger(`${eventName}:${this.id}`, evtParams);
  }
}

_.extend(Panel.prototype, Backbone.Events);
